(() => {
  'use strict';

  const API_KEY = 'c2bDocumentToc';

  if (window[API_KEY]?.initialize) {
    window[API_KEY].initialize(document);
    return;
  }

  const MINIMUM_HEADINGS = 3;
  const ENHANCED_ATTRIBUTE = 'data-c2b-document-enhanced';
  const ACTIVE_LINK_CLASS = 'c2b-document-toc__link--active';
  const DEFAULT_ACTIVATION_OFFSET = 120;
  const tocTrackers = new WeakMap();

  const slugify = (value) => {
    const source = String(value || '');
    const normalized = (typeof source.normalize === 'function' ? source.normalize('NFD') : source)
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || 'sectiune';
  };

  const assignHeadingIds = (headings) => {
    const headingSet = new Set(headings);
    const usedIds = new Set(
      Array.from(document.querySelectorAll('[id]'))
        .filter((element) => !headingSet.has(element))
        .map((element) => element.id)
        .filter(Boolean),
    );

    headings.forEach((heading) => {
      const existingId = heading.id.trim();

      if (existingId && !usedIds.has(existingId)) {
        usedIds.add(existingId);
        return;
      }

      const baseId = slugify(heading.textContent);
      let candidateId = baseId;
      let suffix = 2;

      while (usedIds.has(candidateId)) {
        candidateId = `${baseId}-${suffix}`;
        suffix += 1;
      }

      heading.id = candidateId;
      usedIds.add(candidateId);
    });
  };

  const getActivationOffset = (heading) => {
    if (!heading || typeof window.getComputedStyle !== 'function') {
      return DEFAULT_ACTIVATION_OFFSET;
    }

    const scrollMarginTop = Number.parseFloat(window.getComputedStyle(heading).scrollMarginTop);

    return Number.isFinite(scrollMarginTop) && scrollMarginTop > 0
      ? scrollMarginTop
      : DEFAULT_ACTIVATION_OFFSET;
  };

  const keepActiveLinkVisible = ({ toc, link }) => {
    if (!toc || !link || toc.scrollHeight <= toc.clientHeight) return;

    const tocRect = toc.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    const edgePadding = 8;

    if (linkRect.top < tocRect.top + edgePadding) {
      toc.scrollTop -= tocRect.top - linkRect.top + edgePadding;
      return;
    }

    if (linkRect.bottom > tocRect.bottom - edgePadding) {
      toc.scrollTop += linkRect.bottom - tocRect.bottom + edgePadding;
    }
  };

  const updateTocObserver = ({ headings, nav, toc }) => {
    if (!headings?.length || !nav || !toc) return;

    tocTrackers.get(toc)?.disconnect();

    const links = Array.from(nav.querySelectorAll('.c2b-document-toc__link'));
    const items = links
      .map((link, index) => {
        const heading = headings[index];
        return heading ? { heading, link } : null;
      })
      .filter(Boolean);

    if (!items.length) return;

    let animationFrame = 0;
    let activeIndex = -1;

    const setActiveLink = (nextIndex) => {
      if (nextIndex === activeIndex) return;

      activeIndex = nextIndex;

      items.forEach(({ link }, index) => {
        const isActive = index === activeIndex;
        link.classList.toggle(ACTIVE_LINK_CLASS, isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'location');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      keepActiveLinkVisible({ toc, link: items[activeIndex]?.link });
    };

    const calculateActiveSection = () => {
      animationFrame = 0;

      const activationOffset = getActivationOffset(items[0].heading);
      let nextIndex = 0;

      items.forEach(({ heading }, index) => {
        if (heading.getBoundingClientRect().top <= activationOffset) {
          nextIndex = index;
        }
      });

      const documentElement = document.documentElement;
      const reachedPageEnd =
        window.scrollY + window.innerHeight >= documentElement.scrollHeight - 4;

      if (reachedPageEnd) {
        nextIndex = items.length - 1;
      }

      setActiveLink(nextIndex);
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(calculateActiveSection);
    };

    const handleNavClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest('.c2b-document-toc__link');

      if (!link || !nav.contains(link)) return;

      const nextIndex = items.findIndex((item) => item.link === link);
      if (nextIndex >= 0) setActiveLink(nextIndex);
    };

    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('load', scheduleUpdate, { once: true });
    nav.addEventListener('click', handleNavClick);

    const tracker = {
      disconnect() {
        window.removeEventListener('scroll', scheduleUpdate);
        window.removeEventListener('resize', scheduleUpdate);
        window.removeEventListener('load', scheduleUpdate);
        nav.removeEventListener('click', handleNavClick);

        if (animationFrame) {
          window.cancelAnimationFrame(animationFrame);
          animationFrame = 0;
        }

        items.forEach(({ link }) => {
          link.classList.remove(ACTIVE_LINK_CLASS);
          link.removeAttribute('aria-current');
        });

        tocTrackers.delete(toc);
      },
    };

    tocTrackers.set(toc, tracker);
    scheduleUpdate();
  };

  const disconnectToc = (toc) => {
    tocTrackers.get(toc)?.disconnect();
  };

  const disconnect = (scope = document) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    if (scope instanceof Element && scope.matches('[data-c2b-document-toc]')) {
      disconnectToc(scope);
    }

    scope.querySelectorAll('[data-c2b-document-toc]').forEach(disconnectToc);
  };

  const populateToc = ({ content, toc, aside, layout }) => {
    if (!content || !toc || !aside || !layout) return false;

    const headings = Array.from(content.querySelectorAll('h2, h3')).filter(
      (heading) => heading.textContent.trim() !== '',
    );

    if (headings.length < MINIMUM_HEADINGS) return false;

    assignHeadingIds(headings);

    const nav = toc.querySelector('[data-c2b-document-toc-nav]');
    if (!nav) return false;

    nav.replaceChildren();

    headings.forEach((heading) => {
      const link = document.createElement('a');
      link.className = 'c2b-document-toc__link';
      link.textContent = heading.textContent.trim();
      link.setAttribute('href', `#${heading.id}`);

      if (heading.tagName === 'H3') {
        link.classList.add('c2b-document-toc__link--h3');
      }

      nav.append(link);
    });

    toc.hidden = false;
    aside.hidden = false;
    layout.classList.remove('c2b-policy-layout--single');
    updateTocObserver({ headings, nav, toc });
    return true;
  };

  const enhanceCustomDocument = (root) => {
    if (!root || root.hasAttribute(ENHANCED_ATTRIBUTE)) return;

    root.setAttribute(ENHANCED_ATTRIBUTE, 'true');

    populateToc({
      content: root.querySelector('[data-c2b-document-content]'),
      toc: root.querySelector('[data-c2b-document-toc]'),
      aside: root.querySelector('[data-c2b-document-aside]'),
      layout: root.querySelector('[data-c2b-document-layout]'),
    });
  };

  const createNativeToc = () => {
    const aside = document.createElement('aside');
    aside.className = 'c2b-document-aside';
    aside.setAttribute('aria-label', 'Cuprins');
    aside.hidden = true;

    const toc = document.createElement('div');
    toc.className = 'c2b-document-toc';
    toc.setAttribute('data-c2b-document-toc', '');
    toc.hidden = true;

    const title = document.createElement('h2');
    title.id = 'c2b-native-document-toc-title';
    title.className = 'c2b-document-toc__title';
    title.textContent = 'Pe această pagină';

    const nav = document.createElement('nav');
    nav.className = 'c2b-document-toc__nav';
    nav.setAttribute('data-c2b-document-toc-nav', '');
    nav.setAttribute('aria-labelledby', title.id);

    toc.append(title, nav);
    aside.append(toc);

    return { aside, toc };
  };

  const enhanceNativePolicy = () => {
    if (!document.body.classList.contains('c2b-policy-page')) return;

    const container = document.querySelector('.shopify-policy__container');
    if (!container || container.hasAttribute(ENHANCED_ATTRIBUTE)) return;

    container.setAttribute(ENHANCED_ATTRIBUTE, 'true');

    const policyTitle = container.querySelector('.shopify-policy__title');
    const policyBody = container.querySelector('.shopify-policy__body');
    const content = policyBody?.querySelector('.rte') || policyBody;
    const fallback = document.querySelector('[data-c2b-document-date-fallback]');
    const updated =
      container.querySelector('.c2b-document-updated') ||
      fallback?.querySelector('.c2b-document-updated');

    if (updated && policyTitle) {
      policyTitle.append(updated);
    }

    if (fallback) fallback.remove();
    if (!policyBody || !content) return;

    const nativeHeadings = Array.from(content.querySelectorAll('h2, h3')).filter(
      (heading) => heading.textContent.trim() !== '',
    );

    if (nativeHeadings.length < MINIMUM_HEADINGS) return;

    const layout = document.createElement('div');
    layout.className = 'c2b-document-layout--native';

    const { aside, toc } = createNativeToc();
    policyBody.before(layout);
    layout.append(aside, policyBody);

    const hasToc = populateToc({ content, toc, aside, layout });

    if (!hasToc) {
      layout.before(policyBody);
      layout.remove();
    }
  };

  const initialize = (scope = document) => {
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    if (scope instanceof Element && scope.matches('[data-c2b-document]')) {
      enhanceCustomDocument(scope);
    }

    scope.querySelectorAll('[data-c2b-document]').forEach(enhanceCustomDocument);
    enhanceNativePolicy();
  };

  window[API_KEY] = { disconnect, initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => disconnect(event.target));
})();
